import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RealtimeCodeEditor from '../components/RealtimeCodeEditor';
import axios from '../api/axios';
import SaveIcon from '@mui/icons-material/Save';

export default function EditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [manualSave, setManualSave] = useState(null);
  const [currentCode, setCurrentCode] = useState('');
  // Check if we're joining an existing room or creating a new one
  const existingRoomCode = searchParams.get('room');
  const generatedRoomCode = React.useMemo(() => `private-room-${Math.random().toString(36).substr(2, 9)}`, []);
  const roomCode = existingRoomCode || generatedRoomCode;
  
  // Generate shareable link
  const shareableLink = session ? `${window.location.origin}/codeshare/${session.room_code}` : '';

  useEffect(() => {
    if (existingRoomCode) {
      joinExistingSession();
    } else {
      createSession();
    }
  }, [roomCode]);

  const createSession = async () => {
    try {
      const response = await axios.post('/interview-sessions/quick-start', {
        room_code: roomCode,
      });
      setSession(response.data.session);
      setLoading(false);
      setCurrentCode('-- Start coding here...');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create interview session');
      setSnackbar({
        open: true,
        message: 'Failed to start session. Redirecting...',
        severity: 'error'
      });
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const joinExistingSession = async () => {
    try {
      // Get existing session by room code
      const response = await axios.get(`/interview-sessions/room/${existingRoomCode}`);
      const sessionData = response.data;
      console.log('Session data:', sessionData);     
      setSession(sessionData);
      setLoading(false);
      
      // Load existing code if available
      if (sessionData.code_editor_data && sessionData.code_editor_data.length > 0) {
        // Get the latest code editor data
        const latestCodeData = sessionData.code_editor_data[0];
        setCurrentCode(latestCodeData.code_content);
      } else {
        setCurrentCode('-- Start coding here...');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join session');
      setSnackbar({
        open: true,
        message: 'Session not found or access denied. Redirecting...',
        severity: 'error'
      });
      setTimeout(() => navigate('/sessions'), 2000);
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm('Are you sure you want to end this interview session? All participants will be disconnected.')) {
      return;
    }

    console.log('Ending session...', { socket, session });

    try {
      // Save current code before ending session
      handleSaveCode();

      // Notify all participants via Socket.IO FIRST
      if (socket && socket.emit) {
        console.log('Notifying participants via Socket.IO...');
        socket.emit('custom:event', {
          authToken: localStorage.getItem('token'),
          event: 'sessionEnded',
          channel: session.room_code,
          data: {
            message: 'The interviewer has ended this session. Thank you for your participation!',
            endedBy: 'interviewer'
          }
        });
      } else {
        console.log('Socket not available for notification');
      }

      // End session in backend
      console.log('Calling backend to end session...');
      await axios.post(`/interview-sessions/end-by-room/${session.room_code}`);

      setSnackbar({
        open: true,
        message: 'Session ended successfully',
        severity: 'success'
      });

      // Redirect to dashboard
      setTimeout(() => navigate('/sessions'), 1500);
    } catch (err) {
      console.error('End session error:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to end session',
        severity: 'error'
      });
    }
  };

  const handleSocketReady = useCallback((socketInstance, manualSaveFn) => {
    setSocket(socketInstance);
    if (manualSaveFn) {
      setManualSave(() => manualSaveFn);
    }
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareableLink).then(() => {
      setSnackbar({
        open: true,
        message: 'Interview link copied to clipboard!',
        severity: 'success'
      });
    }).catch(() => {
      setSnackbar({
        open: true,
        message: 'Failed to copy link',
        severity: 'error'
      });
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Code Interview',
          text: 'You have been invited to join a coding interview session',
          url: shareableLink,
        });
        setSnackbar({
          open: true,
          message: 'Link shared successfully!',
          severity: 'success'
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const handleSaveCode = async () => {
    console.log('Saving code...', currentCode);
    await axios.post('/code-editor-data', {
      interview_session_id: session?.id,
      language: 'sql',
      code_content: currentCode,
      cursor_positions: null, // Could be enhanced to track cursor positions
      selection_data: null,   // Could be enhanced to track selections
      editor_settings: {
        theme: 'vs-dark',
        fontSize: 16,
        minimap: false
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Starting interview session...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" sx={{ minHeight: '48px' }}>
        <Toolbar sx={{ minHeight: '48px !important', py: 0.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => navigate('/sessions')}
            sx={{ mr: 1, p: 0.5 }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontSize: '0.9rem' }}>
            Code Share Session
          </Typography>
          <Chip
            label={`${session?.room_code}`}
            size="small"
            sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.75rem', height: '24px' }}
          />
          <Tooltip title="Copy link">
            <IconButton
              color="inherit"
              onClick={handleCopyLink}
              sx={{ mr: 0.5, p: 0.5 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share link">
            <IconButton
              color="inherit"
              onClick={handleShare}
              sx={{ mr: 0.5, p: 0.5 }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            color="inherit"
            variant="outlined"
            onClick={handleEndSession}
            size="small"
            sx={{ borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', py: 0.5, px: 1 }}
          >
            End
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl" sx={{ flex: 1, py: 1, overflow: 'auto' }}>
        <Alert severity="info" sx={{ mb: 1, py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: 'fit-content' }}>
              Share link:
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'rgba(0,0,0,0.05)',
                p: 0.5,
                borderRadius: 0.5,
                flex: 1,
                fontSize: '0.75rem',
                minWidth: '200px'
              }}
            >
              {shareableLink}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyLink}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
            >
              Copy
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveCode}
              sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
            >
              Save
            </Button>
          </Box>
        </Alert>
        <RealtimeCodeEditor 
          room={session?.room_code} 
          userName={session?.company?.name || 'Interviewer'}
          userType="interviewer"
          sessionId={session?.id}
          onSocketReady={handleSocketReady}
          currentCode={currentCode}
          setCurrentCode={setCurrentCode}
        />
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

